import { useEffect, useMemo } from 'react'
import { Box, Button, Typography, Stack } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { Home as HomeIcon, ArrowBack as ArrowBackIcon, LinkOff as LinkOffIcon, Storefront as StorefrontIcon } from '@mui/icons-material'
import { poolwerxColors } from '../theme/colors'
import StandaloneCardLayout from '../components/layouts/StandaloneCardLayout'
import { primaryTealButtonSx, secondaryTealButtonSx } from '../components/auth/authButtonStyles'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '../contexts/AuthContext'
import { hasAccessToFp } from '../services/auth'
import { parseInspectorRef, generateInspectorUrl } from '../utils/urls'
import { auditApi } from '../services/auditApi'
import FranchiseAccessDenied from '../components/inspector/FranchiseAccessDenied'

export default function NotFound() {
  const { isAuthenticated, user } = useAuth()

  const isAdminUser = user?.role && ['EVO_ADMIN', 'EVO_TEAM', 'PSC_TEAM'].includes(user.role)

  // FP ref from an /inspect/<ref>/... dead URL, if any. The segment may be a
  // bare code or the `<code>-<id>` form; parse to the bare code (lowercased so
  // a mixed-case URL still matches the lowercase fp_codes in franchise_access)
  // and keep the tenure id to re-pin the recovery link.
  const inspectRef = useMemo(() => {
    const match = window.location.pathname.match(/^\/inspect\/([^/]+)/)
    return match ? parseInspectorRef(match[1].toLowerCase()) : null
  }, [])
  const inspectFpCode = inspectRef?.fpCode ?? null

  // When the user can reach that FP, point them at its dashboard rather than
  // the generic home action. (No-access case is handled by the early return.)
  const accessibleFp = useMemo(() => {
    if (!inspectFpCode || !user || !hasAccessToFp(user, inspectFpCode)) return null
    const name = user.franchise_access?.find(fa => fa.fp_code === inspectFpCode)?.franchise_name
    return { fpCode: inspectFpCode, tenureId: inspectRef?.tenureId, name }
  }, [inspectFpCode, inspectRef, user])

  // Fire-and-forget audit of the dead URL so support can see stale-bookmark /
  // broken-link hits. Authenticated-only (the endpoint requires a session);
  // errors are swallowed inside auditApi so the 404 page never itself errors.
  useEffect(() => {
    if (!isAuthenticated) return
    auditApi.reportRouteNotFound(window.location.pathname + window.location.search)
  }, [isAuthenticated])

  const homePath = !isAuthenticated ? '/login' : isAdminUser ? '/admin' : '/'

  // FP-scoped dead URL the user can't reach: mirror InspectorLayout's gate so
  // /inspect/<fp>/<bad-subpath> shows "Access Denied" like /inspect/<fp>/ does,
  // instead of a bare "Page Unavailable" that hides the real reason.
  if (inspectFpCode && user && !hasAccessToFp(user, inspectFpCode)) {
    return <FranchiseAccessDenied user={user} fpCode={inspectFpCode} />
  }

  return (
    <>
      <Helmet>
        <title>Page Unavailable | Inspector Tool</title>
      </Helmet>
      <StandaloneCardLayout
        headerTitle="Page Unavailable"
        headerIcon={<LinkOffIcon sx={{ fontSize: 48 }} />}
      >
        <Stack spacing={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              The page may have moved, expired, or the link may be incorrect.
            </Typography>
          </Box>

          <Stack spacing={2}>
            {/* Primary action first */}
            {accessibleFp ? (
              <Button
                variant="contained"
                size="large"
                fullWidth
                component={RouterLink}
                to={generateInspectorUrl(accessibleFp.fpCode, { id: accessibleFp.tenureId })}
                startIcon={<StorefrontIcon />}
                sx={primaryTealButtonSx}
              >
                {accessibleFp.name ? `Go to ${accessibleFp.name} Dashboard` : 'Go to Franchise Dashboard'}
              </Button>
            ) : (
              <Button
                variant="contained"
                size="large"
                fullWidth
                component={RouterLink}
                to={homePath}
                startIcon={<HomeIcon />}
                sx={primaryTealButtonSx}
              >
                {isAuthenticated && isAdminUser ? 'Go to Admin Support Console' : isAuthenticated ? 'Continue' : 'Go to Login'}
              </Button>
            )}

            {/* Secondary action */}
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={() => window.history.back()}
              startIcon={<ArrowBackIcon />}
              sx={secondaryTealButtonSx}
            >
              Go Back
            </Button>

            {accessibleFp && isAdminUser && (
              <Button
                variant="outlined"
                size="large"
                fullWidth
                component={RouterLink}
                to="/admin"
                startIcon={<HomeIcon />}
                sx={secondaryTealButtonSx}
              >
                Go to Admin Support Console
              </Button>
            )}
          </Stack>

          <Box sx={{ pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center' }}>
              Need help? 
              <a 
                href="https://poolwerx.freshdesk.com/a/tickets/new" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: poolwerxColors.teal, textDecoration: 'underline', marginLeft: '4px' }}
              >
                Submit a support ticket
              </a>
            </Typography>
          </Box>
        </Stack>
      </StandaloneCardLayout>
    </>
  )
}